import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import userRewardsService from "./userRewardsService";
import { isAdminUser } from "../utils/access";
import { toast } from "react-toastify";
import { useLocalStorage } from "@uidotdev/usehooks";
import "./UserRewards.css";

const ITEM_TYPES = ["certificate_band", "gift", "other"];
const ITEM_TYPE_LABELS = {
  certificate_band: "Certificate + Band",
  gift: "Gift",
  other: "Other",
  certificate: "Other",
  band: "Other",
};

const normalizeRewardType = (type) =>
  ITEM_TYPES.includes(type) ? type : "other";

const rewardTypeLabel = (type) => ITEM_TYPE_LABELS[type] || "Other";

const idOf = (value) =>
  value?.id ??
  value?.stage_id ??
  value?.stageId ??
  value?.specialization_id ??
  value?.specializationId ??
  value?.skill_id ??
  value?.skillId;

const nameOf = (value) =>
  value?.name ??
  value?.specialization_name ??
  value?.specializationName ??
  value?.skill_name ??
  value?.skillName ??
  value?.stage_name ??
  value?.stageName ??
  "Unnamed";

const sameId = (left, right) => String(left) === String(right);

const parseJson = (value, fallback) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return fallback;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

// The existing stages endpoint returns an object with a `stages` property,
// while some deployments return the array directly. Keep this feature tolerant
// of both response shapes before using array methods such as slice/map.
const asStageList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.stages)) return value.stages;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const errorMessage = (error, fallback) => {
  const status = error?.response?.status;
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You do not have permission to manage User Rewards.";
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
};

const emptyForm = (specializationId = "") => ({
  specializationId,
  milestoneType: "stage",
  skillId: "",
  stageId: "",
  items: [{ type: "certificate_band", name: "", imageUrl: "" }],
});

const getRewardItems = (milestone) => {
  let bundle = milestone?.reward_bundle || milestone?.rewardBundle || {};
  if (typeof bundle === "string") {
    try {
      bundle = JSON.parse(bundle);
    } catch {
      bundle = {};
    }
  }
  return Array.isArray(bundle.items)
      ? bundle.items.map((item) => ({
        ...item,
        type: normalizeRewardType(item?.type || item?.item_type),
        imageUrl: item?.imageUrl || item?.image_url || "",
      }))
    : [];
};

const getSkillIds = (specialization) => {
  const candidates = [
    specialization?.skill_ids,
    specialization?.skillIds,
    specialization?.skills,
  ];
  const value = candidates.find((candidate) => candidate != null);
  const parsed = parseJson(value, value);
  if (!Array.isArray(parsed)) return [];
  return parsed.map((skill) => idOf(skill) ?? skill).filter(Boolean);
};

const UserRewards = () => {
  const [token] = useLocalStorage("token", null);
  const [specializations, setSpecializations] = useState([]);
  const [skills, setSkills] = useState([]);
  const [stagesBySkill, setStagesBySkill] = useState({});
  const [selectedSpecializationId, setSelectedSpecializationId] = useState("");
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState({ catalog: true, milestones: false, stages: false });
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [copying, setCopying] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [milestonesError, setMilestonesError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingMilestone, setEditingMilestone] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [initialForm, setInitialForm] = useState(emptyForm());
  const [formErrors, setFormErrors] = useState({});
  const [copyForm, setCopyForm] = useState({ source: "", target: "" });
  const [copyError, setCopyError] = useState("");

  const activeSpecializations = useMemo(
    () => specializations.filter((specialization) => specialization?.is_active !== 0),
    [specializations]
  );

  const selectedSpecialization = useMemo(
    () => activeSpecializations.find((item) => sameId(idOf(item), selectedSpecializationId)),
    [activeSpecializations, selectedSpecializationId]
  );

  const availableSkills = useMemo(() => {
    if (!selectedSpecialization) return [];
    const skillIds = getSkillIds(selectedSpecialization);
    if (skillIds.length) {
      return skills
        .filter((skill) => skillIds.some((skillId) => sameId(skillId, idOf(skill))))
        .sort((left, right) => skillIds.indexOf(idOf(left)) - skillIds.indexOf(idOf(right)));
    }
    const related = skills.filter((skill) =>
      [skill?.specialization_id, skill?.specializationId, skill?.specialisation_id].some(
        (specializationId) => sameId(specializationId, selectedSpecializationId)
      )
    );
    return related.length ? related : skills;
  }, [selectedSpecialization, selectedSpecializationId, skills]);

  const loadMilestones = useCallback(
    async (specializationId = selectedSpecializationId) => {
      if (!specializationId) {
        setMilestones([]);
        return;
      }
      setLoading((previous) => ({ ...previous, milestones: true }));
      setMilestonesError("");
      try {
        const result = await userRewardsService.getMilestones(token, specializationId);
        setMilestones(Array.isArray(result) ? result : []);
      } catch (error) {
        setMilestones([]);
        setMilestonesError(errorMessage(error, "Unable to load configured milestones."));
      } finally {
        setLoading((previous) => ({ ...previous, milestones: false }));
      }
    },
    [selectedSpecializationId, token]
  );

  const loadCatalog = useCallback(async () => {
    setLoading((previous) => ({ ...previous, catalog: true }));
    setCatalogError("");
    try {
      const [specializationData, skillData] = await Promise.all([
        userRewardsService.getSpecializations(token),
        userRewardsService.getSkills(token),
      ]);
      const active = (specializationData || []).filter((item) => item?.is_active !== 0);
      setSpecializations(active);
      setSkills(skillData || []);
      setSelectedSpecializationId((current) => current || idOf(active[0]) || "");
    } catch (error) {
      setCatalogError(errorMessage(error, "Unable to load specializations and skills."));
    } finally {
      setLoading((previous) => ({ ...previous, catalog: false }));
    }
  }, [token]);

  useEffect(() => {
    if (isAdminUser()) loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (selectedSpecializationId && !loading.catalog) loadMilestones();
  }, [loadMilestones, loading.catalog, selectedSpecializationId]);

  useEffect(() => {
    let cancelled = false;
    const loadStages = async () => {
      if (!availableSkills.length) return;
      setLoading((previous) => ({ ...previous, stages: true }));
      try {
        const entries = await Promise.all(
          availableSkills.map(async (skill) => [
            idOf(skill),
            asStageList(await userRewardsService.getStages(token, idOf(skill))),
          ])
        );
        if (!cancelled) setStagesBySkill((previous) => ({ ...previous, ...Object.fromEntries(entries) }));
      } catch (error) {
        if (!cancelled) toast.error(errorMessage(error, "Unable to load stages for this specialization."));
      } finally {
        if (!cancelled) setLoading((previous) => ({ ...previous, stages: false }));
      }
    };
    loadStages();
    return () => {
      cancelled = true;
    };
  }, [availableSkills, token]);

  useEffect(() => {
    const warn = (event) => {
      if (formOpen && formDirty(form, initialForm)) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [form, formOpen, initialForm]);

  const selectedStages = asStageList(stagesBySkill[form.skillId]);

  const openAddDialog = () => {
    setEditingMilestone(null);
    const nextForm = emptyForm(selectedSpecializationId);
    setForm(nextForm);
    setInitialForm(nextForm);
    setFormErrors({});
    setFormOpen(true);
  };

  const openEditDialog = (milestone) => {
    setEditingMilestone(milestone);
    const nextForm = {
      specializationId: selectedSpecializationId,
      milestoneType: milestone.milestone_type === "skill" ? "skill" : "stage",
      skillId: milestone.skill_id ?? "",
      stageId: milestone.stage_id ?? "",
      items: getRewardItems(milestone).map((item) => ({
        type: normalizeRewardType(item.type),
        name: item.name || "",
        imageUrl: item.imageUrl || item.image_url || "",
      })),
    };
    setForm(nextForm);
    setInitialForm(nextForm);
    setFormErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    if (formDirty(form, initialForm) && !window.confirm("Discard your unsaved reward changes?")) return;
    setFormOpen(false);
  };

  const updateItem = (index, field, value) => {
    setForm((previous) => ({
      ...previous,
      items: previous.items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.specializationId) errors.specializationId = "Select a specialization.";
    if (!form.skillId) errors.skillId = "Select a skill.";
    if (form.milestoneType === "stage" && !form.stageId) errors.stageId = "Select a stage.";
    if (form.milestoneType === "skill" && form.stageId) errors.stageId = "Skill milestones cannot have a stage.";
    if (!form.items.length) errors.items = "Add at least one reward item.";
    if (form.items.some((item) => !ITEM_TYPES.includes(item.type))) errors.items = "Choose a supported reward type.";
    if (form.items.some((item) => !item.name.trim())) errors.items = "Every reward item needs a name.";
    const targetKey = `${form.milestoneType}:${form.skillId}:${form.milestoneType === "stage" ? form.stageId : ""}`;
    const duplicate = milestones.some((milestone) => {
      const existingKey = `${milestone.milestone_type}:${milestone.skill_id}:${milestone.milestone_type === "stage" ? milestone.stage_id : ""}`;
      return existingKey === targetKey && milestone.milestone_id !== editingMilestone?.milestone_id;
    });
    if (duplicate) errors.target = "A reward is already configured for this milestone.";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveForm = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      await userRewardsService.saveMilestone(token, {
        specializationId: form.specializationId,
        milestoneType: form.milestoneType,
        skillId: form.skillId,
        stageId: form.milestoneType === "stage" ? form.stageId : null,
        rewardBundle: {
          items: form.items.map(({ type, name, imageUrl }) => ({ type, name: name.trim(), imageUrl: imageUrl.trim() })),
        },
      });
      toast.success(editingMilestone ? "Reward milestone updated." : "Reward milestone created.");
      setFormOpen(false);
      await loadMilestones();
    } catch (error) {
      setFormErrors({ submit: errorMessage(error, "Unable to save this reward milestone.") });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.milestone_id);
    try {
      await userRewardsService.deactivateMilestone(token, deleteTarget.milestone_id);
      toast.success("Reward milestone deactivated.");
      setDeleteTarget(null);
      await loadMilestones();
    } catch (error) {
      toast.error(errorMessage(error, "Unable to deactivate this milestone."));
    } finally {
      setDeletingId(null);
    }
  };

  const copyRewards = async () => {
    setCopyError("");
    if (!copyForm.source || !copyForm.target) {
      setCopyError("Select both a source and target specialization.");
      return;
    }
    if (sameId(copyForm.source, copyForm.target)) {
      setCopyError("Source and target specializations must be different.");
      return;
    }
    setCopying(true);
    try {
      const result = await userRewardsService.copyRewards(token, copyForm.source, copyForm.target);
      const copied = result?.copied ?? result?.data?.copied ?? 0;
      toast.success(`${copied} reward milestone${copied === 1 ? "" : "s"} copied.`);
      setCopyOpen(false);
      setSelectedSpecializationId(String(copyForm.target));
      await loadMilestones(copyForm.target);
    } catch (error) {
      setCopyError(errorMessage(error, "The reward structure could not be copied."));
    } finally {
      setCopying(false);
    }
  };

  const handleSpecializationChange = (value) => {
    setSelectedSpecializationId(value ? idOf(value) : "");
    setMilestones([]);
    setMilestonesError("");
    setEditingMilestone(null);
    setForm(emptyForm(value ? idOf(value) : ""));
    setInitialForm(emptyForm(value ? idOf(value) : ""));
    setFormErrors({});
  };

  const orderedMilestones = useMemo(() => {
    const skillPosition = (skillId) => {
      const position = availableSkills.findIndex((skill) => sameId(idOf(skill), skillId));
      return position === -1 ? Number.MAX_SAFE_INTEGER : position;
    };
    const stagePosition = (milestone) => {
      const stages = asStageList(stagesBySkill[milestone.skill_id]);
      const position = stages.findIndex((stage) => sameId(idOf(stage), milestone.stage_id));
      return position === -1 ? Number.MAX_SAFE_INTEGER : position;
    };
    return [...milestones].sort((left, right) =>
      skillPosition(left.skill_id) - skillPosition(right.skill_id) ||
      (left.milestone_type === "skill" ? 1 : 0) - (right.milestone_type === "skill" ? 1 : 0) ||
      stagePosition(left) - stagePosition(right)
    );
  }, [availableSkills, milestones, stagesBySkill]);

  if (!isAdminUser()) {
    return <div className="user-rewards-page"><Alert severity="error">You are not authorized to access User Rewards.</Alert></div>;
  }

  if (loading.catalog) {
    return <div className="user-rewards-page user-rewards-centered"><CircularProgress aria-label="Loading User Rewards" /></div>;
  }

  const skillName = (skillId) => nameOf(availableSkills.find((skill) => sameId(idOf(skill), skillId)));
  const stageName = (skillId, stageId) => nameOf(asStageList(stagesBySkill[skillId]).find((stage) => sameId(idOf(stage), stageId)));

  return (
    <div className="user-rewards-page">
      <Card className="user-rewards-card" elevation={2}>
        <CardContent>
          <div className="user-rewards-header">
            <div>
              <Typography variant="h4" component="h1">User Rewards</Typography>
              <Typography color="text.secondary">Configure automatic certificate, band, gift, and other rewards for specialization milestones.</Typography>
            </div>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => { setCopyForm({ source: selectedSpecializationId, target: "" }); setCopyError(""); setCopyOpen(true); }}>Copy Rewards</Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog} disabled={!selectedSpecializationId}>Add Reward</Button>
            </Stack>
          </div>

          <div className="user-rewards-toolbar">
            <Autocomplete
              options={activeSpecializations}
              value={selectedSpecialization || null}
              getOptionLabel={nameOf}
              isOptionEqualToValue={(option, value) => sameId(idOf(option), idOf(value))}
              onChange={(_, value) => handleSpecializationChange(value)}
              renderInput={(params) => <TextField {...params} label="Specialization" placeholder="Search specializations" />}
              sx={{ minWidth: { xs: "100%", sm: 360 } }}
            />
            <div className="user-rewards-count"><span>Total configured milestones</span><strong>{milestones.length}</strong></div>
          </div>

          {catalogError && <Alert severity="error" action={<Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={loadCatalog}>Retry</Button>}>{catalogError}</Alert>}
          {!catalogError && activeSpecializations.length === 0 && <Alert severity="info">No active specializations are available.</Alert>}
          {milestonesError && <Alert severity="error" action={<Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={() => loadMilestones()}>Retry</Button>}>{milestonesError}</Alert>}

          <div className="user-rewards-table-wrap">
            {loading.milestones ? <div className="user-rewards-centered table-loader"><CircularProgress aria-label="Loading milestones" /></div> : milestones.length === 0 ? (
              <div className="user-rewards-empty"><Typography variant="h6">No reward milestones configured</Typography><Typography color="text.secondary">Add a stage or skill-completion reward for {nameOf(selectedSpecialization)}.</Typography><Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>Add Reward</Button></div>
            ) : (
              <table className="user-rewards-table">
                <thead><tr><th>Milestone type</th><th>Skill</th><th>Stage</th><th>Reward items</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>{orderedMilestones.map((milestone) => {
                  const items = getRewardItems(milestone);
                  return <tr key={milestone.milestone_id}>
                    <td data-label="Milestone type"><Chip size="small" label={milestone.milestone_type === "skill" ? "Skill completion" : "Stage"} color={milestone.milestone_type === "skill" ? "secondary" : "primary"} /></td>
                    <td data-label="Skill">{skillName(milestone.skill_id)}</td>
                    <td data-label="Stage">{milestone.milestone_type === "skill" ? "Complete" : stageName(milestone.skill_id, milestone.stage_id)}</td>
                    <td data-label="Reward items"><div className="reward-items">{items.map((item, index) => <div className="reward-item" key={`${item.type}-${index}`}>{item.imageUrl && <img src={item.imageUrl} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} />}<span><b>{rewardTypeLabel(item.type)}</b>{item.name}</span></div>)}</div></td>
                    <td data-label="Status"><Chip size="small" label={Number(milestone.is_active) === 1 || milestone.is_active === true ? "Active" : "Inactive"} color={Number(milestone.is_active) === 1 || milestone.is_active === true ? "success" : "default"} /></td>
                    <td data-label="Actions"><div className="user-rewards-actions"><IconButton aria-label={`Edit ${milestone.milestone_key || "milestone"}`} onClick={() => openEditDialog(milestone)}><EditOutlinedIcon /></IconButton><IconButton color="error" aria-label={`Deactivate ${milestone.milestone_key || "milestone"}`} onClick={() => setDeleteTarget(milestone)} disabled={deletingId === milestone.milestone_id}>{deletingId === milestone.milestone_id ? <CircularProgress size={20} /> : <DeleteOutlineIcon />}</IconButton></div></td>
                  </tr>;
                })}</tbody>
              </table>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={formOpen} onClose={closeForm} fullWidth maxWidth="md" aria-labelledby="reward-form-title">
        <DialogTitle id="reward-form-title">{editingMilestone ? "Edit reward milestone" : "Add reward milestone"}</DialogTitle>
        <DialogContent dividers>
          {formErrors.submit && <Alert severity="error" sx={{ mb: 2 }}>{formErrors.submit}</Alert>}
          {formErrors.target && <Alert severity="error" sx={{ mb: 2 }}>{formErrors.target}</Alert>}
          <Stack spacing={2}>
            <TextField label="Specialization" value={nameOf(selectedSpecialization)} InputProps={{ readOnly: true }} />
            <FormControl fullWidth error={Boolean(formErrors.milestoneType)}><InputLabel id="milestone-type-label">Milestone type</InputLabel><Select labelId="milestone-type-label" label="Milestone type" value={form.milestoneType} onChange={(event) => setForm((previous) => ({ ...previous, milestoneType: event.target.value, stageId: "" }))}><MenuItem value="stage">Stage</MenuItem><MenuItem value="skill">Skill completion</MenuItem></Select></FormControl>
            <FormControl fullWidth error={Boolean(formErrors.skillId)}><InputLabel id="reward-skill-label">Skill</InputLabel><Select labelId="reward-skill-label" label="Skill" value={form.skillId} onChange={(event) => setForm((previous) => ({ ...previous, skillId: event.target.value, stageId: "" }))}><MenuItem value=""><em>Select a skill</em></MenuItem>{availableSkills.map((skill) => <MenuItem key={idOf(skill)} value={idOf(skill)}>{nameOf(skill)}</MenuItem>)}</Select>{formErrors.skillId && <FormHelperText>{formErrors.skillId}</FormHelperText>}</FormControl>
            {form.milestoneType === "stage" && <FormControl fullWidth error={Boolean(formErrors.stageId)} disabled={!form.skillId || loading.stages}><InputLabel id="reward-stage-label">Stage</InputLabel><Select labelId="reward-stage-label" label="Stage" value={form.stageId} onChange={(event) => setForm((previous) => ({ ...previous, stageId: event.target.value }))}><MenuItem value=""><em>{loading.stages ? "Loading stages…" : "Select a stage"}</em></MenuItem>{selectedStages.slice().sort((a, b) => Number(a.stage_order ?? a.stageOrder ?? 0) - Number(b.stage_order ?? b.stageOrder ?? 0)).map((stage) => <MenuItem key={idOf(stage)} value={idOf(stage)}>{nameOf(stage)}</MenuItem>)}</Select>{formErrors.stageId && <FormHelperText>{formErrors.stageId}</FormHelperText>}</FormControl>}
            <div><Typography variant="subtitle1" component="h3">Reward bundle items</Typography><Typography variant="body2" color="text.secondary">A certificate and band can be configured together as one milestone.</Typography></div>
            {form.items.map((item, index) => <div className="reward-editor-item" key={index}><FormControl fullWidth><InputLabel id={`reward-type-${index}`}>Item type</InputLabel><Select labelId={`reward-type-${index}`} label="Item type" value={item.type} onChange={(event) => updateItem(index, "type", event.target.value)}>{ITEM_TYPES.map((type) => <MenuItem key={type} value={type}>{rewardTypeLabel(type)}</MenuItem>)}</Select></FormControl><TextField fullWidth required label="Reward name" value={item.name} onChange={(event) => updateItem(index, "name", event.target.value)} error={Boolean(formErrors.items) && !item.name.trim()} /><TextField fullWidth label="Image URL (optional)" value={item.imageUrl} onChange={(event) => updateItem(index, "imageUrl", event.target.value)} /><div className="reward-preview">{item.imageUrl ? <img src={item.imageUrl} alt="Reward preview" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : <div className="reward-preview-placeholder">{rewardTypeLabel(item.type).slice(0, 1)}</div>}<span>{item.name || "Reward preview"}</span></div><IconButton aria-label={`Remove reward item ${index + 1}`} onClick={() => setForm((previous) => ({ ...previous, items: previous.items.filter((_, itemIndex) => itemIndex !== index) }))} disabled={form.items.length === 1}><DeleteOutlineIcon /></IconButton></div>)}
            {formErrors.items && <FormHelperText error>{formErrors.items}</FormHelperText>}
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setForm((previous) => ({ ...previous, items: [...previous.items, { type: "gift", name: "", imageUrl: "" }] }))}>Add reward item</Button>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={closeForm} disabled={saving}>Cancel</Button><Button variant="contained" onClick={saveForm} disabled={saving}>{saving ? <CircularProgress size={22} color="inherit" /> : editingMilestone ? "Save changes" : "Create reward"}</Button></DialogActions>
      </Dialog>

      <Dialog open={copyOpen} onClose={() => !copying && setCopyOpen(false)} fullWidth maxWidth="sm" aria-labelledby="copy-rewards-title">
        <DialogTitle id="copy-rewards-title">Copy Rewards</DialogTitle>
        <DialogContent dividers><Stack spacing={2}><Alert severity="warning">Matching target milestones may be overwritten. User progress and awards will not be copied.</Alert>{copyError && <Alert severity="error">{copyError}</Alert>}<FormControl fullWidth><InputLabel id="copy-source-label">Source specialization</InputLabel><Select labelId="copy-source-label" label="Source specialization" value={copyForm.source} onChange={(event) => setCopyForm((previous) => ({ ...previous, source: event.target.value }))}>{activeSpecializations.map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{nameOf(item)}</MenuItem>)}</Select></FormControl><FormControl fullWidth><InputLabel id="copy-target-label">Target specialization</InputLabel><Select labelId="copy-target-label" label="Target specialization" value={copyForm.target} onChange={(event) => setCopyForm((previous) => ({ ...previous, target: event.target.value }))}><MenuItem value=""><em>Select a target</em></MenuItem>{activeSpecializations.filter((item) => !sameId(idOf(item), copyForm.source)).map((item) => <MenuItem key={idOf(item)} value={idOf(item)}>{nameOf(item)}</MenuItem>)}</Select></FormControl>{copyForm.source && copyForm.target && <Typography variant="body2">Copying rewards from <strong>{nameOf(activeSpecializations.find((item) => sameId(idOf(item), copyForm.source)))}</strong> to <strong>{nameOf(activeSpecializations.find((item) => sameId(idOf(item), copyForm.target)))}</strong>.</Typography>}</Stack></DialogContent>
        <DialogActions><Button onClick={() => setCopyOpen(false)} disabled={copying}>Cancel</Button><Button variant="contained" onClick={copyRewards} disabled={copying || !copyForm.source || !copyForm.target}>{copying ? <CircularProgress size={22} color="inherit" /> : "Copy rewards"}</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deletingId && setDeleteTarget(null)} aria-labelledby="deactivate-reward-title"><DialogTitle id="deactivate-reward-title">Deactivate this reward milestone?</DialogTitle><DialogContent><Typography color="text.secondary">This will soft-delete the configuration. It will no longer be active for future milestone awards.</Typography></DialogContent><DialogActions><Button onClick={() => setDeleteTarget(null)} disabled={Boolean(deletingId)}>Cancel</Button><Button color="error" variant="contained" onClick={deactivate} disabled={Boolean(deletingId)}>{deletingId ? <CircularProgress size={22} color="inherit" /> : "Deactivate"}</Button></DialogActions></Dialog>
    </div>
  );
};

const formDirty = (form, initialForm) => {
  return JSON.stringify(form) !== JSON.stringify(initialForm);
};

export default UserRewards;
